import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

// "Something wrong?": anyone on the desk reports information that turned out wrong after a manual check. The report
// keeps what the screen showed (snapshot) so the rule behind it can be found; managers work them in the Reports tab;
// the reporter sees when it was closed.
export const REPORT_TYPES = [
  ['status', 'Wrong status or stage'],
  ['courier', 'Wrong courier info'],
  ['metres', 'Wrong metres or fabric'],
  ['invoice', 'Wrong invoice / Zoho reason'],
  ['due', 'Wrong due date'],
  ['list_wrong', "Shouldn't be in this list"],
  ['list_missing', 'Missing from a list'],
  ['batch', 'Wrong batch info'],
  ['other', 'Other'],
];
const SECTION_LABEL = { courier: 'courier', invoice: 'invoice', fabrics: 'fabrics', next: 'next step', batch: 'batch', due: 'due date' };
const SECTION_TYPE = { courier: 'courier', invoice: 'invoice', fabrics: 'metres', next: 'status', batch: 'batch', due: 'due' };
const CLOSE = [['fixed', 'Fixed (rule changed)'], ['data_fixed', 'Data fixed (one-off)'], ['not_bug', 'Not a bug']];
const STATUS_LABEL = { open: 'Open', fixing: 'Fixing', fixed: 'Fixed', data_fixed: 'Data fixed', not_bug: 'Not a bug' };
const when = (t, time) => (t ? new Date(t).toLocaleString('en-IN', { day: 'numeric', month: 'short', ...(time ? { hour: 'numeric', minute: '2-digit' } : {}), timeZone: 'Asia/Kolkata' }) : '');

const ReportCtx = React.createContext(null);

// Wraps an order or batch screen: section flags open the form preset to that section; the form sits at the bottom
export function ReportArea({ api, toast, kind, refId, number, where = null, children }) {
  const [open, setOpen] = useState(false);
  const [section, setSection] = useState(null);
  const [type, setType] = useState('');
  const [truth, setTruth] = useState('');
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const boxRef = useRef(null);
  useEffect(() => { setOpen(false); setSent(false); setTruth(''); setType(''); setSection(null); }, [refId]);
  const start = useCallback((sec = null) => {
    setSection(sec); setType(SECTION_TYPE[sec] || ''); setSent(false); setOpen(true);
    setTimeout(() => boxRef.current?.scrollIntoView?.({ block: 'center', behavior: 'smooth' }), 30);
  }, []);
  const ctx = useMemo(() => ({ start }), [start]);
  const send = async () => {
    setBusy(true);
    try {
      const row = where?.row || {};
      await api('/api/v1/order-desk/reports', 'POST', {
        kind, refId, number, section, type, truth, queue: where?.queue || null,
        snapshot: { stage: row.stage, due: row.due, status: row.status, side: row.side, chips: row.chips, title: where?.title || null },
      });
      setSent(true); setOpen(false); setTruth(''); setType('');
      toast?.('Thanks: reported. You will see here when it is fixed.');
    } catch (e) { toast?.(e.message, true); } finally { setBusy(false); }
  };
  return (
    <ReportCtx.Provider value={ctx}>
      {children}
      <div className="od-report" ref={boxRef}>
        {!open ? (
          <button className="od-report-link" onClick={() => start(null)}>{sent ? 'Reported · report something else?' : 'Something wrong here?'}</button>
        ) : (
          <section className="od-report-form" aria-label="Report wrong information">
            <div className="od-report-h"><b>Report wrong information</b>{section && <span className="od-small">about: {SECTION_LABEL[section] || section}</span>}</div>
            <div className="od-report-types" role="radiogroup" aria-label="What is wrong">
              {REPORT_TYPES.map(([k, label]) => <button key={k} role="radio" aria-checked={type === k} className={type === k ? 'on' : ''} onClick={() => setType(k)}>{label}</button>)}
            </div>
            <label className="od-label" htmlFor={`od-rep-${refId}`}>What is actually true? {type === 'other' ? '' : '(optional)'}</label>
            <textarea id={`od-rep-${refId}`} className="od-input" rows={2} value={truth} onChange={(e) => setTruth(e.target.value)} placeholder="e.g. Parcel was delivered on 10 Oct, customer confirmed" />
            <p className="od-small">What this screen shows now is saved with the report.</p>
            <div className="od-actions">
              <button className="od-btn od-pri" disabled={busy || !type || (type === 'other' && truth.trim().length < 3)} onClick={send}>{busy ? 'Sending…' : 'Send report'}</button>
              <button className="od-btn" onClick={() => setOpen(false)}>Cancel</button>
            </div>
          </section>
        )}
      </div>
    </ReportCtx.Provider>
  );
}

// Small flag on a section header: opens the report form preset to that section
export function ReportFlag({ section }) {
  const ctx = useContext(ReportCtx);
  if (!ctx) return null;
  return (
    <button type="button" className="od-flag" title="Something wrong here? Report it" aria-label={`Report wrong ${SECTION_LABEL[section] || section} information`} onClick={(e) => { e.stopPropagation(); ctx.start(section); }}>
      <svg viewBox="0 0 16 16" width="13" height="13" aria-hidden="true"><path d="M3 14V2.5M3 2.5h8.5l-1.6 3 1.6 3H3" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
    </button>
  );
}

// "Your report was fixed" notices (closed reports the reporter has not seen)
export function ReportUpdates({ api, onOpen }) {
  const [list, setList] = useState([]);
  useEffect(() => {
    let alive = true;
    api('/api/v1/order-desk/reports/updates').then((r) => alive && setList(r.updates || [])).catch(() => {});
    return () => { alive = false; };
  }, [api]);
  if (!list.length) return null;
  const seen = async (ids) => { setList((l) => l.filter((x) => !ids.includes(x.id))); try { await api('/api/v1/order-desk/reports/updates/seen', 'POST', { ids }); } catch (e) { /* shown again next time */ } };
  return (
    <div className="od-rep-updates" role="status">
      {list.slice(0, 3).map((u) => (
        <div key={u.id} className="od-rep-update">
          <span><b>Your report on {u.kind === 'order' ? `#${u.number}` : u.number}</b> ({u.typeLabel}) · {STATUS_LABEL[u.status]}{u.closeNote ? `: ${u.closeNote}` : ''} <small>{u.closedBy} · {when(u.closedAtMs)}</small></span>
          <button className="od-link" onClick={() => onOpen?.(u)}>Open</button>
          <button className="od-link" onClick={() => seen([u.id])}>OK</button>
        </div>
      ))}
      {list.length > 3 && <button className="od-link" onClick={() => seen(list.map((x) => x.id))}>Mark all {list.length} seen</button>}
    </div>
  );
}

// Reports tab (managers): counts first, then each report with what the screen showed and the close actions
export function ReportsView({ api, toast, onOpen }) {
  const [tab, setTab] = useState('open');
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [notes, setNotes] = useState({});
  const [busy, setBusy] = useState(null);
  const [reload, setReload] = useState(0);
  useEffect(() => {
    let alive = true;
    setErr(null);
    api(`/api/v1/order-desk/reports?status=${tab}`).then((r) => alive && setData(r)).catch((e) => alive && setErr(e.message));
    return () => { alive = false; };
  }, [api, tab, reload]);
  const move = async (id, status) => {
    setBusy(id);
    try { await api(`/api/v1/order-desk/reports/${id}`, 'POST', { status, note: notes[id] || '' }); toast?.(`Marked ${STATUS_LABEL[status].toLowerCase()}`); setNotes((n) => ({ ...n, [id]: '' })); setReload((x) => x + 1); } catch (e) { toast?.(e.message, true); } finally { setBusy(null); }
  };
  if (err) return <div className="od-empty"><b>Couldn't load reports</b>{err}</div>;
  if (!data) return <div className="od-skel" aria-busy="true" />;
  const st = data.stats;
  return (
    <div className="od-det od-reports">
      <header className="od-bh">
        <div className="od-bh-top"><h2>Reports</h2><span className="od-small">wrong information found by the team</span></div>
        <div className="od-bh-stats">
          <div className={st.open ? 'pend' : ''}><b>{st.open}</b><small>Open</small></div>
          <div><b>{st.thisWeek}</b><small>This week</small></div>
          <div><b>{st.avgDaysToFix ?? '—'}{st.avgDaysToFix != null ? ' d' : ''}</b><small>Avg to fix</small></div>
          <div><b>{st.notBugShare}%</b><small>Not a bug</small></div>
        </div>
      </header>
      {(st.weekByType.length > 0 || st.openByType.length > 0) && (
        <section className="od-rep-counts">
          {st.weekByType.length > 0 && <div><small>This week by type</small><ul>{st.weekByType.map(([k, n]) => <li key={k}><span>{k}</span><b>{n}</b></li>)}</ul></div>}
          {st.weekByQueue.length > 0 && <div><small>This week by list</small><ul>{st.weekByQueue.map(([k, n]) => <li key={k}><span>{k}</span><b>{n}</b></li>)}</ul></div>}
        </section>
      )}
      <div className="od-stage-tabs" role="tablist" aria-label="Report status">
        {[['open', 'Open'], ['closed', 'Closed'], ['all', 'All']].map(([k, l]) => <button key={k} role="tab" aria-selected={tab === k} onClick={() => setTab(k)}>{l}</button>)}
      </div>
      {data.reports.length === 0 && <div className="od-empty"><b>No reports here</b>{tab === 'open' ? 'Nothing open: the team has not reported anything wrong.' : ''}</div>}
      <ul className="od-rep-list">
        {data.reports.map((r) => {
          const snap = r.snapshot || {};
          const closed = ['fixed', 'data_fixed', 'not_bug'].includes(r.status);
          return (
            <li key={r.id} className={`od-rep ${r.status}`}>
              <div className="od-rep-top">
                <button className="od-link od-mono" onClick={() => onOpen?.(r)}>{r.kind === 'order' ? `#${r.number}` : r.number}</button>
                <span className="od-rep-type">{r.typeLabel}</span>
                <span className={`od-rep-st ${r.status}`}>{STATUS_LABEL[r.status]}</span>
                <small>{r.by} · {when(r.createdAtMs, true)}{r.queue ? ` · from ${r.queue}` : ''}{r.section ? ` · ${r.section}` : ''}</small>
              </div>
              {r.truth && <p className="od-rep-truth">“{r.truth}”</p>}
              <p className="od-rep-snap"><span>Screen showed:</span> {[snap.stage, snap.status, snap.due, snap.side, ...(snap.chips || [])].filter(Boolean).join(' · ') || '—'}
                {r.server && <> · <span>saved data:</span> {Object.entries(r.server).filter(([, v]) => v).map(([k, v]) => `${k} ${v}`).join(' · ')}</>}</p>
              {closed ? (
                <p className="od-small">{STATUS_LABEL[r.status]} by {r.closedBy} · {when(r.closedAtMs)}{r.closeNote ? `: ${r.closeNote}` : ''} <button className="od-link" disabled={busy === r.id} onClick={() => move(r.id, 'open')}>Reopen</button></p>
              ) : (
                <div className="od-rep-act">
                  <input className="od-input" aria-label={`What was done for report on ${r.number}`} value={notes[r.id] || ''} onChange={(e) => setNotes((n) => ({ ...n, [r.id]: e.target.value }))} placeholder="One line: what was done (needed to close)" />
                  {r.status === 'open' && <button className="od-btn" disabled={busy === r.id} onClick={() => move(r.id, 'fixing')}>Fixing</button>}
                  {CLOSE.map(([k, l]) => <button key={k} className={`od-btn${k === 'fixed' ? ' od-pri' : ''}`} disabled={busy === r.id || (notes[r.id] || '').trim().length < 3} onClick={() => move(r.id, k)}>{l}</button>)}
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
